import React, { useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Form,
  Modal,
  Nav,
  Spinner,
  Tab,
} from "react-bootstrap";
import { Plus } from "react-feather";
import CreatableSelect from "react-select/creatable";
import { useGet, usePost } from "../../api";
import {
  IKindredBrandDetail,
  IKindredDealDetail,
  IKindredCouponDetail,
  IKindredOfferDetail,
  KindredPublishStatus,
  KindredCouponCoverage,
} from "../../interfaces";
import {
  BrandTypeahead,
  TagListEditor,
  MetadataEditor,
} from "../../components/workbench";

type EntityType = "brand" | "deal" | "coupon" | "offer";

/** Source for promos added by hand in the dashboard. */
export const DEFAULT_MANUAL_SOURCE = "Kindred";

export const SourcePicker: React.FC<{
  value: string;
  onChange: (v: string) => void;
}> = ({ value, onChange }) => {
  const { data: networks } = useGet<string[]>(
    "kindred/reference/networks",
    true
  );
  // Keep the current value selectable even if it predates the reference list.
  const options = React.useMemo(() => {
    const list = networks ?? [];
    const all = value && !list.includes(value) ? [value, ...list] : list;
    return all.map((n) => ({ value: n, label: n }));
  }, [networks, value]);

  return (
    <CreatableSelect
      options={options}
      value={value ? { value, label: value } : null}
      onChange={(opt) => onChange(opt?.value ?? "")}
      placeholder="Search or type a source..."
      formatCreateLabel={(input) => `Use "${input}"`}
      isClearable
      styles={{
        control: (base) => ({
          ...base,
          minHeight: 31,
          fontSize: "0.875rem",
        }),
        menu: (base) => ({ ...base, fontSize: "0.875rem" }),
      }}
    />
  );
};

interface CreateEntityModalProps {
  show: boolean;
  onHide: () => void;
  onCreated: (type: EntityType, id: string) => void;
  /** Restrict the modal to one entity type and hide the tab bar. */
  only?: EntityType;
  /** Seed values for the create form, e.g. the domain/country in context. */
  prefill?: Record<string, any>;
}

const publishedStatusOptions = [
  { value: KindredPublishStatus.Draft, label: "Draft" },
  { value: KindredPublishStatus.AwaitingApproval, label: "Awaiting Approval" },
  { value: KindredPublishStatus.Approved, label: "Approved" },
  { value: KindredPublishStatus.Deactivated, label: "Deactivated" },
];

// ── Brand Create Form ────────────────────────────────────

const BrandCreateForm: React.FC<{
  onSubmit: (data: any, comment: string) => Promise<void>;
  loading: boolean;
}> = ({ onSubmit, loading }) => {
  const [name, setName] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [logo, setLogo] = useState("");
  const [comment, setComment] = useState("");

  const canSubmit = name.trim() && comment.trim();

  return (
    <>
      <Form.Group className="mb-2">
        <Form.Label className="small mb-0">
          Name <span className="text-danger">*</span>
        </Form.Label>
        <Form.Control
          size="sm"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Brand name"
        />
      </Form.Group>
      <Form.Group className="mb-2">
        <Form.Label className="small mb-0">Country Code</Form.Label>
        <Form.Control
          size="sm"
          value={countryCode}
          onChange={(e) => setCountryCode(e.target.value)}
          placeholder="e.g. GB"
        />
      </Form.Group>
      <Form.Group className="mb-2">
        <Form.Label className="small mb-0">Logo URL</Form.Label>
        <Form.Control
          size="sm"
          value={logo}
          onChange={(e) => setLogo(e.target.value)}
          placeholder="https://..."
        />
      </Form.Group>
      <hr />
      <Form.Group className="mb-2">
        <Form.Label className="small mb-0">
          Comment <span className="text-danger">*</span>
        </Form.Label>
        <Form.Control
          size="sm"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Reason for creating..."
        />
      </Form.Group>
      <Button
        variant="primary"
        size="sm"
        disabled={!canSubmit || loading}
        onClick={() =>
          onSubmit(
            {
              name: name.trim(),
              countryCode: countryCode || null,
              logo: logo || null,
            },
            comment.trim()
          )
        }
      >
        {loading && <Spinner animation="border" size="sm" className="me-1" />}
        <Plus size={12} className="me-1" />
        Create Brand
      </Button>
    </>
  );
};

// ── Deal Create Form ─────────────────────────────────────

const DealCreateForm: React.FC<{
  onSubmit: (data: any, comment: string) => Promise<void>;
  loading: boolean;
}> = ({ onSubmit, loading }) => {
  const [form, setForm] = useState<Record<string, any>>({
    name: "",
    brandId: "",
    brandName: "",
    publishedStatus: KindredPublishStatus.Draft,
    source: DEFAULT_MANUAL_SOURCE,
    description: "",
    shopLink: "",
    startDate: "",
    endDate: "",
    suspended: false,
    featured: false,
    exclusive: false,
    domains: [],
    countries: [],
  });
  const [comment, setComment] = useState("");

  const canSubmit =
    form.name?.trim() &&
    form.brandId &&
    form.startDate &&
    form.endDate &&
    comment.trim();

  return (
    <>
      <Form.Group className="mb-2">
        <Form.Label className="small mb-0">
          Name <span className="text-danger">*</span>
        </Form.Label>
        <Form.Control
          size="sm"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
      </Form.Group>

      <BrandTypeahead
        brandId={form.brandId}
        brandName={form.brandName}
        onChange={(brandId, brandName) =>
          setForm({ ...form, brandId, brandName })
        }
      />

      <Form.Group className="mb-2">
        <Form.Label className="small mb-0">Published Status</Form.Label>
        <Form.Select
          size="sm"
          value={form.publishedStatus}
          onChange={(e) =>
            setForm({ ...form, publishedStatus: Number(e.target.value) })
          }
        >
          {publishedStatusOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Form.Select>
      </Form.Group>

      <Form.Group className="mb-2">
        <Form.Label className="small mb-0">Source</Form.Label>
        <SourcePicker
          value={form.source}
          onChange={(source) => setForm({ ...form, source })}
        />
      </Form.Group>

      <Form.Group className="mb-2">
        <Form.Label className="small mb-0">Description</Form.Label>
        <Form.Control
          as="textarea"
          size="sm"
          rows={2}
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />
      </Form.Group>

      <Form.Group className="mb-2">
        <Form.Label className="small mb-0">Shop Link</Form.Label>
        <Form.Control
          size="sm"
          value={form.shopLink}
          onChange={(e) => setForm({ ...form, shopLink: e.target.value })}
        />
      </Form.Group>

      <Form.Group className="mb-2">
        <Form.Label className="small mb-0">
          Start Date <span className="text-danger">*</span>
        </Form.Label>
        <Form.Control
          size="sm"
          type="date"
          value={form.startDate}
          onChange={(e) =>
            setForm({ ...form, startDate: e.target.value || undefined })
          }
        />
      </Form.Group>

      <Form.Group className="mb-2">
        <Form.Label className="small mb-0">
          End Date <span className="text-danger">*</span>
        </Form.Label>
        <Form.Control
          size="sm"
          type="date"
          value={form.endDate}
          onChange={(e) =>
            setForm({ ...form, endDate: e.target.value || undefined })
          }
        />
      </Form.Group>

      <Form.Check
        type="switch"
        label="Suspended"
        className="mb-1"
        checked={!!form.suspended}
        onChange={(e) => setForm({ ...form, suspended: e.target.checked })}
      />
      <Form.Check
        type="switch"
        label="Featured"
        className="mb-1"
        checked={!!form.featured}
        onChange={(e) => setForm({ ...form, featured: e.target.checked })}
      />
      <Form.Check
        type="switch"
        label="Exclusive"
        className="mb-2"
        checked={!!form.exclusive}
        onChange={(e) => setForm({ ...form, exclusive: e.target.checked })}
      />

      <TagListEditor
        label="Domains"
        normalize="lower"
        items={form.domains}
        onChange={(domains) => setForm({ ...form, domains })}
        placeholder="Add domain..."
      />

      <TagListEditor
        label="Countries"
        items={form.countries}
        onChange={(countries) => setForm({ ...form, countries })}
        placeholder="Add country code..."
      />

      <hr />
      <Form.Group className="mb-2">
        <Form.Label className="small mb-0">
          Comment <span className="text-danger">*</span>
        </Form.Label>
        <Form.Control
          size="sm"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Reason for creating..."
        />
      </Form.Group>
      <Button
        variant="primary"
        size="sm"
        disabled={!canSubmit || loading}
        onClick={() => {
          const { brandName, ...data } = form;
          onSubmit(
            {
              ...data,
              startDate: new Date(data.startDate).toISOString(),
              endDate: new Date(data.endDate).toISOString(),
            },
            comment.trim()
          );
        }}
      >
        {loading && <Spinner animation="border" size="sm" className="me-1" />}
        <Plus size={12} className="me-1" />
        Create Deal
      </Button>
    </>
  );
};

// ── Coupon Create Form ───────────────────────────────────

const CouponCreateForm: React.FC<{
  onSubmit: (data: any, comment: string) => Promise<void>;
  loading: boolean;
  prefill?: Record<string, any>;
}> = ({ onSubmit, loading, prefill }) => {
  const [form, setForm] = useState<Record<string, any>>({
    code: "",
    brandId: "",
    brandName: "",
    discount: 0,
    summary: "",
    validFrom: "",
    validTo: "",
    publishedStatus: KindredPublishStatus.Draft,
    priority: 0,
    coverage: KindredCouponCoverage.None,
    source: DEFAULT_MANUAL_SOURCE,
    domains: [],
    countries: [],
    metadata: {},
    ...prefill,
  });
  const [comment, setComment] = useState("");

  const canSubmit =
    form.code?.trim() &&
    form.brandId &&
    form.validFrom &&
    form.validTo &&
    comment.trim();

  return (
    <>
      <Form.Group className="mb-2">
        <Form.Label className="small mb-0">
          Code <span className="text-danger">*</span>
        </Form.Label>
        <Form.Control
          size="sm"
          value={form.code}
          onChange={(e) => setForm({ ...form, code: e.target.value })}
        />
      </Form.Group>

      <BrandTypeahead
        brandId={form.brandId}
        brandName={form.brandName}
        onChange={(brandId, brandName) =>
          setForm({ ...form, brandId, brandName })
        }
      />

      <Form.Group className="mb-2">
        <Form.Label className="small mb-0">Published Status</Form.Label>
        <Form.Select
          size="sm"
          value={form.publishedStatus}
          onChange={(e) =>
            setForm({ ...form, publishedStatus: Number(e.target.value) })
          }
        >
          {publishedStatusOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Form.Select>
      </Form.Group>

      <Form.Group className="mb-2">
        <Form.Label className="small mb-0">Discount</Form.Label>
        <Form.Control
          size="sm"
          type="number"
          value={form.discount}
          onChange={(e) =>
            setForm({ ...form, discount: Number(e.target.value) })
          }
        />
      </Form.Group>

      <Form.Group className="mb-2">
        <Form.Label className="small mb-0">Summary</Form.Label>
        <Form.Control
          as="textarea"
          size="sm"
          rows={2}
          value={form.summary}
          onChange={(e) => setForm({ ...form, summary: e.target.value })}
        />
      </Form.Group>

      <Form.Group className="mb-2">
        <Form.Label className="small mb-0">
          Valid From <span className="text-danger">*</span>
        </Form.Label>
        <Form.Control
          size="sm"
          type="date"
          value={form.validFrom}
          onChange={(e) =>
            setForm({ ...form, validFrom: e.target.value || undefined })
          }
        />
      </Form.Group>

      <Form.Group className="mb-2">
        <Form.Label className="small mb-0">
          Valid To <span className="text-danger">*</span>
        </Form.Label>
        <Form.Control
          size="sm"
          type="date"
          value={form.validTo}
          onChange={(e) =>
            setForm({ ...form, validTo: e.target.value || undefined })
          }
        />
      </Form.Group>

      <Form.Group className="mb-2">
        <Form.Label className="small mb-0">Priority</Form.Label>
        <Form.Control
          size="sm"
          type="number"
          value={form.priority}
          onChange={(e) =>
            setForm({ ...form, priority: Number(e.target.value) })
          }
        />
      </Form.Group>

      <Form.Group className="mb-2">
        <Form.Label className="small mb-0">Coverage</Form.Label>
        <Form.Select
          size="sm"
          value={form.coverage}
          onChange={(e) =>
            setForm({ ...form, coverage: Number(e.target.value) })
          }
        >
          <option value={KindredCouponCoverage.None}>Not Set</option>
          <option value={KindredCouponCoverage.SiteWide}>Site-Wide</option>
          <option value={KindredCouponCoverage.Category}>Category</option>
          <option value={KindredCouponCoverage.Product}>
            Product Specific
          </option>
        </Form.Select>
      </Form.Group>

      <Form.Group className="mb-2">
        <Form.Label className="small mb-0">Source</Form.Label>
        <SourcePicker
          value={form.source}
          onChange={(source) => setForm({ ...form, source })}
        />
      </Form.Group>

      <TagListEditor
        label="Domains"
        normalize="lower"
        items={form.domains}
        onChange={(domains) => setForm({ ...form, domains })}
        placeholder="Add domain..."
      />

      <TagListEditor
        label="Countries"
        items={form.countries}
        onChange={(countries) => setForm({ ...form, countries })}
        placeholder="Add country code..."
      />

      <MetadataEditor
        metadata={form.metadata}
        onChange={(metadata) => setForm({ ...form, metadata })}
      />

      <hr />
      <Form.Group className="mb-2">
        <Form.Label className="small mb-0">
          Comment <span className="text-danger">*</span>
        </Form.Label>
        <Form.Control
          size="sm"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Reason for creating..."
        />
      </Form.Group>
      <Button
        variant="primary"
        size="sm"
        disabled={!canSubmit || loading}
        onClick={() => {
          const { brandName, ...data } = form;
          const hasMetadata =
            data.metadata && Object.keys(data.metadata).length > 0;
          onSubmit(
            {
              ...data,
              validFrom: new Date(data.validFrom).toISOString(),
              validTo: new Date(data.validTo).toISOString(),
              metadata: hasMetadata ? data.metadata : undefined,
            },
            comment.trim()
          );
        }}
      >
        {loading && <Spinner animation="border" size="sm" className="me-1" />}
        <Plus size={12} className="me-1" />
        Create Coupon
      </Button>
    </>
  );
};

// ── Offer Create Form ────────────────────────────────────

const OfferCreateForm: React.FC<{
  onSubmit: (data: any, comment: string) => Promise<void>;
  loading: boolean;
}> = ({ onSubmit, loading }) => {
  const [form, setForm] = useState<Record<string, any>>({
    brandId: "",
    brandName: "",
    summary: "",
    validFrom: "",
    validTo: "",
    publishedStatus: KindredPublishStatus.Draft,
    priority: 0,
    source: DEFAULT_MANUAL_SOURCE,
    trackingLink: "",
    promoLink: "",
    domains: [],
    countries: [],
    metadata: {},
  });
  const [comment, setComment] = useState("");

  const canSubmit =
    form.brandId && form.validFrom && form.validTo && comment.trim();

  return (
    <>
      <BrandTypeahead
        brandId={form.brandId}
        brandName={form.brandName}
        onChange={(brandId, brandName) =>
          setForm({ ...form, brandId, brandName })
        }
      />

      <Form.Group className="mb-2">
        <Form.Label className="small mb-0">Published Status</Form.Label>
        <Form.Select
          size="sm"
          value={form.publishedStatus}
          onChange={(e) =>
            setForm({ ...form, publishedStatus: Number(e.target.value) })
          }
        >
          {publishedStatusOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Form.Select>
      </Form.Group>

      <Form.Group className="mb-2">
        <Form.Label className="small mb-0">Summary</Form.Label>
        <Form.Control
          as="textarea"
          size="sm"
          rows={2}
          value={form.summary}
          onChange={(e) => setForm({ ...form, summary: e.target.value })}
        />
      </Form.Group>

      <Form.Group className="mb-2">
        <Form.Label className="small mb-0">
          Valid From <span className="text-danger">*</span>
        </Form.Label>
        <Form.Control
          size="sm"
          type="date"
          value={form.validFrom}
          onChange={(e) =>
            setForm({ ...form, validFrom: e.target.value || undefined })
          }
        />
      </Form.Group>

      <Form.Group className="mb-2">
        <Form.Label className="small mb-0">
          Valid To <span className="text-danger">*</span>
        </Form.Label>
        <Form.Control
          size="sm"
          type="date"
          value={form.validTo}
          onChange={(e) =>
            setForm({ ...form, validTo: e.target.value || undefined })
          }
        />
      </Form.Group>

      <Form.Group className="mb-2">
        <Form.Label className="small mb-0">Priority</Form.Label>
        <Form.Control
          size="sm"
          type="number"
          value={form.priority}
          onChange={(e) =>
            setForm({ ...form, priority: Number(e.target.value) })
          }
        />
      </Form.Group>

      <Form.Group className="mb-2">
        <Form.Label className="small mb-0">Source</Form.Label>
        <SourcePicker
          value={form.source}
          onChange={(source) => setForm({ ...form, source })}
        />
      </Form.Group>

      <Form.Group className="mb-2">
        <Form.Label className="small mb-0">Tracking Link</Form.Label>
        <Form.Control
          size="sm"
          value={form.trackingLink}
          onChange={(e) => setForm({ ...form, trackingLink: e.target.value })}
        />
      </Form.Group>

      <Form.Group className="mb-2">
        <Form.Label className="small mb-0">Promo Link</Form.Label>
        <Form.Control
          size="sm"
          value={form.promoLink}
          onChange={(e) => setForm({ ...form, promoLink: e.target.value })}
        />
      </Form.Group>

      <TagListEditor
        label="Domains"
        normalize="lower"
        items={form.domains}
        onChange={(domains) => setForm({ ...form, domains })}
        placeholder="Add domain..."
      />

      <TagListEditor
        label="Countries"
        items={form.countries}
        onChange={(countries) => setForm({ ...form, countries })}
        placeholder="Add country code..."
      />

      <MetadataEditor
        metadata={form.metadata}
        onChange={(metadata) => setForm({ ...form, metadata })}
      />

      <hr />
      <Form.Group className="mb-2">
        <Form.Label className="small mb-0">
          Comment <span className="text-danger">*</span>
        </Form.Label>
        <Form.Control
          size="sm"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Reason for creating..."
        />
      </Form.Group>
      <Button
        variant="primary"
        size="sm"
        disabled={!canSubmit || loading}
        onClick={() => {
          const { brandName, ...data } = form;
          const hasMetadata =
            data.metadata && Object.keys(data.metadata).length > 0;
          onSubmit(
            {
              ...data,
              summary: data.summary || null,
              validFrom: new Date(data.validFrom).toISOString(),
              validTo: new Date(data.validTo).toISOString(),
              trackingLink: data.trackingLink || null,
              promoLink: data.promoLink || null,
              metadata: hasMetadata ? data.metadata : undefined,
            },
            comment.trim()
          );
        }}
      >
        {loading && <Spinner animation="border" size="sm" className="me-1" />}
        <Plus size={12} className="me-1" />
        Create Offer
      </Button>
    </>
  );
};

// ── Main Modal ───────────────────────────────────────────

export const CreateEntityModal: React.FC<CreateEntityModalProps> = ({
  show,
  onHide,
  onCreated,
  only,
  prefill,
}) => {
  const [activeTab, setActiveTab] = useState<EntityType>(only ?? "brand");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const createBrand = usePost<IKindredBrandDetail, any>();
  const createDeal = usePost<IKindredDealDetail, any>();
  const createCoupon = usePost<IKindredCouponDetail, any>();
  const createOffer = usePost<IKindredOfferDetail, any>();

  const isLoading =
    createBrand.loading ||
    createDeal.loading ||
    createCoupon.loading ||
    createOffer.loading;

  const handleSubmit = async (type: EntityType, data: any, comment: string) => {
    setError(null);
    setSuccess(null);
    try {
      const endpoint = `kindred/${type === "deal" ? "deals" : type === "coupon" ? "coupons" : type === "offer" ? "offers" : "brands"}`;
      const postFn =
        type === "brand"
          ? createBrand
          : type === "deal"
            ? createDeal
            : type === "coupon"
              ? createCoupon
              : createOffer;
      const result = await postFn.execute(endpoint, { data, comment });
      const id = (result as any)?.id;
      setSuccess(
        `${type} created successfully${id ? ` (ID: ${id.substring(0, 12)}...)` : ""}`
      );
      if (id) onCreated(type, id);
    } catch (err: any) {
      setError(err?.message || `Failed to create ${type}`);
    }
  };

  const handleClose = () => {
    setError(null);
    setSuccess(null);
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          {only ? `Create ${only}` : "Create New Entity"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Tab.Container
          activeKey={activeTab}
          onSelect={(k) => k && setActiveTab(k as EntityType)}
        >
          <Nav variant="tabs" className="mb-3">
            {!only && (
              <>
                <Nav.Item>
                  <Nav.Link eventKey="brand">Brand</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="deal">Deal</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="coupon">Coupon</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="offer">Offer</Nav.Link>
                </Nav.Item>
              </>
            )}
          </Nav>

          <Tab.Content>
            <Tab.Pane eventKey="brand">
              <BrandCreateForm
                loading={isLoading}
                onSubmit={(data, comment) =>
                  handleSubmit("brand", data, comment)
                }
              />
            </Tab.Pane>
            <Tab.Pane eventKey="deal">
              <DealCreateForm
                loading={isLoading}
                onSubmit={(data, comment) =>
                  handleSubmit("deal", data, comment)
                }
              />
            </Tab.Pane>
            <Tab.Pane eventKey="coupon">
              <CouponCreateForm
                loading={isLoading}
                prefill={prefill}
                onSubmit={(data, comment) =>
                  handleSubmit("coupon", data, comment)
                }
              />
            </Tab.Pane>
            <Tab.Pane eventKey="offer">
              <OfferCreateForm
                loading={isLoading}
                onSubmit={(data, comment) =>
                  handleSubmit("offer", data, comment)
                }
              />
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </Modal.Body>
    </Modal>
  );
};
